import React, { useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import toast from 'react-hot-toast';
import { 
  faPlus, 
  faSave, 
  faArrowLeft,
  faTrash,
  faCog,
  faRobot,
  faUser,
  faDesktop,
  faChevronDown,
  faChevronRight,
  faSpinner,
  faCheckCircle,
  faFileExport,
  faCopy,
  faEye,
  faProjectDiagram,
  faInfoCircle,
  faTimes,
  faDatabase,
  faTools,
  faServer,
  faCode
} from '@fortawesome/free-solid-svg-icons';

import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import MainLayout from '../Layout/MainLayout';
import { useAuth } from '../../context/AuthContext';
import { workflowAPI } from '../../utils/api';

// Import modular workflow utilities
import {
  STEP_TYPES,
  VALID_STEP_TYPES,
  isStepComplete,
  generateInternalId,
  generateWorkflowYaml,
  processWorkflowImport
} from '../../utils/workflow';

// Import existing modals from utils
import PromptModal from '../../utils/workflow/promptmodal';

// Enhanced Step Card Component with v2 styling
const StepCardV2 = ({ 
  step, 
  stepNumber, 
  isExpanded, 
  onToggleExpand, 
  onDelete, 
  onUpdateStep, 
  onStepTypeChange,
  isFirst,
  isLast,
  onSaveStep,
  prompts,
  onViewPrompt,
  onSelectTools,
  tools,
  workflow
}) => {
  const stepTypeConfig = STEP_TYPES.UI_TEMPLATE.type === step.type ? STEP_TYPES.UI_TEMPLATE :
                        STEP_TYPES.AI.type === step.type ? STEP_TYPES.AI :
                        STEP_TYPES.HUMAN.type === step.type ? STEP_TYPES.HUMAN :
                        STEP_TYPES.BACKEND.type === step.type ? STEP_TYPES.BACKEND : null;

  const getStepIcon = () => {
    switch (step.type) {
      case 'AI': return faRobot;
      case 'Human': return faUser;
      case 'UITemplate': return faDesktop;
      case 'Backend': return faServer;
      default: return faCog;
    }
  };

  const getStepColor = () => {
    switch (step.type) {
      case 'AI': return 'from-purple-500 to-indigo-500';
      case 'Human': return 'from-blue-500 to-cyan-500';
      case 'UITemplate': return 'from-green-500 to-emerald-500';
      case 'Backend': return 'from-orange-500 to-red-500';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow"
    >
      {/* Step Header */}
      <div className={`bg-gradient-to-r ${getStepColor()} p-4`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-white bg-opacity-20 rounded-lg p-2">
              <FontAwesomeIcon icon={getStepIcon()} className="text-white text-xl" />
            </div>
            <div>
              <h3 className="text-white font-bold text-lg">
                Step {stepNumber}: {step.name}
              </h3>
              <p className="text-white text-opacity-90 text-sm">
                {stepTypeConfig?.label || step.type}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={onToggleExpand}
              className="text-white text-opacity-80 hover:text-opacity-100 transition-colors"
            >
              <FontAwesomeIcon 
                icon={isExpanded ? faChevronDown : faChevronRight} 
                className="text-lg"
              />
            </button>
            
            <button
              onClick={() => onSaveStep(step.id)}
              disabled={step.saved}
              className={`px-3 py-1 rounded-md font-medium text-sm transition-colors ${
                step.saved
                  ? 'bg-green-100 text-green-800 cursor-not-allowed'
                  : 'bg-white bg-opacity-20 text-white hover:bg-opacity-30'
              }`}
              title={step.saved ? 'Step saved' : 'Save step'}
            >
              <FontAwesomeIcon icon={step.saved ? faCheckCircle : faSave} className="mr-1" />
              {step.saved ? 'Saved' : 'Save'}
            </button>
            
            <button
              onClick={() => onDelete(step.id)}
              className="text-white text-opacity-80 hover:text-opacity-100 transition-colors"
              title="Delete step"
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </div>
        </div>
      </div>

      {/* Step Content */}
      {isExpanded && (
        <motion.div
          initial={{ height: 0 }}
          animate={{ height: "auto" }}
          exit={{ height: 0 }}
          className="border-t border-gray-200"
        >
          {/* Step Configuration */}
          <div className="p-6 space-y-4">
            {/* Step ID and Name */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Step ID
                </label>
                <input
                  type="text"
                  value={step.id || ''}
                  onChange={(e) => onUpdateStep(step.id, { id: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-sm"
                  placeholder="Internal step ID"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Step Name
                </label>
                <input
                  type="text"
                  value={step.name}
                  onChange={(e) => onUpdateStep(step.id, { name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter step name"
                />
              </div>
            </div>

            {/* Step Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Step Type
              </label>
              <select
                value={step.type}
                onChange={(e) => onStepTypeChange(step.id, e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                {Object.values(STEP_TYPES).map(type => (
                  <option key={type.type} value={type.type}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* AI Step Configuration */}
            {step.type === 'AI' && (
              <div className="space-y-4 bg-purple-50 p-4 rounded-lg">
                <h4 className="font-medium text-purple-900">AI Configuration</h4>
                
                {/* Provider Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    AI Provider
                  </label>
                  <select
                    value={step.config?.provider || 'openai'}
                    onChange={(e) => {
                      // Only allow OpenAI for now
                      if (e.target.value !== 'openai') {
                        toast.error('This provider is not available yet. Only OpenAI is currently supported.');
                        return;
                      }
                      const updatedConfig = {
                        ...step.config,
                        provider: e.target.value,
                        provider_api: e.target.value === 'openai' ? 'responses' : 
                                     e.target.value === 'anthropic' ? 'messages' :
                                     e.target.value === 'google' ? 'generateContent' : ''
                      };
                      onUpdateStep(step.id, { config: updatedConfig });
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    style={{
                      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'
                    }}
                  >
                    <option value="openai">OpenAI</option>
                    <option value="anthropic" disabled>Anthropic (Coming Soon)</option>
                    <option value="google" disabled>Google AI (Coming Soon)</option>
                  </select>
                  <div className="mt-1 text-xs text-gray-500 bg-blue-50 dark:bg-blue-900/20 px-3 py-2 rounded-lg border border-blue-200 dark:border-blue-800">
                    <span className="text-blue-700 dark:text-blue-300">ℹ️</span> Currently, only OpenAI with Responses API is available. More providers coming soon!
                  </div>
                </div>

                {/* Prompt Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FontAwesomeIcon icon={faDatabase} className="mr-2" />
                    Prompt Template
                  </label>
                  <div className="flex space-x-2">
                    <select
                      value={step.config?.prompt_id || ''}
                      onChange={(e) => {
                        const updatedConfig = {
                          ...step.config,
                          prompt_id: e.target.value
                        };
                        onUpdateStep(step.id, { config: updatedConfig });
                      }}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">Select Prompt</option>
                      {prompts.map((prompt) => (
                        <option key={prompt.prompt_id} value={prompt.prompt_id}>
                          {prompt.name}
                        </option>
                      ))}
                    </select>
                    {step.config?.prompt_id && (
                      <button
                        type="button"
                        onClick={() => {
                          const prompt = prompts.find(p => p.prompt_id === step.config.prompt_id);
                          if (prompt) onViewPrompt(prompt);
                        }}
                        className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
                        title="View prompt details"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Tools Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FontAwesomeIcon icon={faTools} className="mr-2" />
                    Available Tools
                  </label>
                  <button
                    onClick={() => onSelectTools(step)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <span>
                      {step.config?.selected_tools && step.config.selected_tools.length > 0
                        ? `${step.config.selected_tools.length} tools selected`
                        : 'Select tools for this AI step'
                      }
                    </span>
                    <FontAwesomeIcon icon={faCog} />
                  </button>
                  {step.config?.selected_tools && step.config.selected_tools.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {step.config.selected_tools.map((toolId, index) => {
                        const tool = tools.find(t => (t.tool_definition_id || t.id) === toolId) ||
                                    tools.find(t => (t.tool_name || t.name) === toolId);
                        return (
                          <span
                            key={index}
                            className="px-2 py-1 bg-blue-100 text-blue-700 rounded-md text-xs"
                          >
                            {tool ? (tool.tool_name || tool.name) : toolId}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Provider API Selection */}
                {step.config?.provider === 'openai' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Provider API
                    </label>
                    <select
                      value={step.config?.provider_api || 'responses'}
                      onChange={(e) => {
                        // Only allow responses API for now
                        if (e.target.value !== 'responses') {
                          toast.error('This API is not available yet. Only Responses API is currently supported.');
                          return;
                        }
                        const updatedConfig = {
                          ...step.config,
                          provider_api: e.target.value
                        };
                        onUpdateStep(step.id, { config: updatedConfig });
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="responses">Responses API</option>
                      <option value="chat" disabled>Chat Completions (Coming Soon)</option>
                      <option value="assistants" disabled>Assistants API (Coming Soon)</option>
                    </select>
                  </div>
                )}
                
                {step.config?.provider === 'anthropic' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Provider API
                    </label>
                    <select
                      value={step.config?.provider_api || 'messages'}
                      onChange={(e) => {
                        const updatedConfig = {
                          ...step.config,
                          provider_api: e.target.value
                        };
                        onUpdateStep(step.id, { config: updatedConfig });
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="messages">Messages API</option>
                      <option value="completions">Completions API</option>
                    </select>
                  </div>
                )}
                
                {step.config?.provider === 'google' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Provider API
                    </label>
                    <select
                      value={step.config?.provider_api || 'generateContent'}
                      onChange={(e) => {
                        const updatedConfig = {
                          ...step.config,
                          provider_api: e.target.value
                        };
                        onUpdateStep(step.id, { config: updatedConfig });
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="generateContent">Generate Content</option>
                      <option value="streamGenerateContent">Stream Generate Content</option>
                    </select>
                  </div>
                )}
              </div>
            )}

            {/* Human Step Configuration */}
            {step.type === 'Human' && (
              <div className="space-y-4 bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium text-blue-900">Human Task Configuration</h4>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assigned Role
                  </label>
                  <input
                    type="text"
                    value={step.config?.role || ''}
                    onChange={(e) => {
                      const updatedConfig = {
                        ...step.config,
                        role: e.target.value
                      };
                      onUpdateStep(step.id, { config: updatedConfig });
                    }}
                    placeholder="e.g., support_agent, sales_agent"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Task Type
                  </label>
                  <input
                    type="text"
                    value={step.config?.task_type || ''}
                    onChange={(e) => {
                      const updatedConfig = {
                        ...step.config,
                        task_type: e.target.value
                      };
                      onUpdateStep(step.id, { config: updatedConfig });
                    }}
                    placeholder="e.g., customer_inquiry, approval_required"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Timeout (hours)
                  </label>
                  <input
                    type="number"
                    value={step.config?.timeout_hours || ''}
                    onChange={(e) => {
                      const updatedConfig = {
                        ...step.config,
                        timeout_hours: parseInt(e.target.value) || 0
                      };
                      onUpdateStep(step.id, { config: updatedConfig });
                    }}
                    placeholder="e.g., 2"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            )}

            {/* UITemplate Step Configuration */}
            {step.type === 'UITemplate' && (
              <div className="space-y-4 bg-green-50 p-4 rounded-lg">
                <h4 className="font-medium text-green-900">UI Template Configuration</h4>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Entry Point Name
                  </label>
                  <input
                    type="text"
                    value={step.config?.entry_point_name || ''}
                    onChange={(e) => {
                      const updatedConfig = {
                        ...step.config,
                        entry_point_name: e.target.value
                      };
                      onUpdateStep(step.id, { config: updatedConfig });
                    }}
                    placeholder="e.g., contact_form_submit"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    API Route
                  </label>
                  <input
                    type="text"
                    value={step.config?.api_route || ''}
                    onChange={(e) => {
                      const updatedConfig = {
                        ...step.config,
                        api_route: e.target.value
                      };
                      onUpdateStep(step.id, { config: updatedConfig });
                    }}
                    placeholder="/api/process/contact-submission"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Redirect Type
                  </label>
                  <select
                    value={step.config?.redirect_type || 'workflow_chat'}
                    onChange={(e) => {
                      const updatedConfig = {
                        ...step.config,
                        redirect_type: e.target.value
                      };
                      onUpdateStep(step.id, { config: updatedConfig });
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  >
                    <option value="workflow_chat">Workflow Chat</option>
                    <option value="custom_url">Custom URL</option>
                    <option value="no_redirect">No Redirect</option>
                  </select>
                </div>
              </div>
            )}

            {/* Backend Step Configuration */}
            {step.type === 'Backend' && (
              <div className="space-y-4 bg-orange-50 p-4 rounded-lg">
                <h4 className="font-medium text-orange-900">Backend Configuration</h4>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Function Name
                  </label>
                  <input
                    type="text"
                    value={step.config?.function || ''}
                    onChange={(e) => {
                      const updatedConfig = {
                        ...step.config,
                        function: e.target.value
                      };
                      onUpdateStep(step.id, { config: updatedConfig });
                    }}
                    placeholder="e.g., create_urgent_ticket"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Parameters (JSON)
                  </label>
                  <textarea
                    value={step.config?.parameters ? JSON.stringify(step.config.parameters, null, 2) : ''}
                    onChange={(e) => {
                      try {
                        const params = e.target.value ? JSON.parse(e.target.value) : {};
                        const updatedConfig = {
                          ...step.config,
                          parameters: params
                        };
                        onUpdateStep(step.id, { config: updatedConfig });
                      } catch (err) {
                        // Invalid JSON, don't update
                      }
                    }}
                    placeholder='{"priority": "high", "auto_notify": true}'
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 font-mono text-sm"
                  />
                </div>
              </div>
            )}

            {/* Action Routing */}
            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Action-Based Routing</h4>
              <div className="space-y-3">
                {Object.entries(step.config?.actionRouting || {}).map(([actionKey, targetStepId]) => (
                  <div key={actionKey} className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={actionKey}
                      onChange={(e) => {
                        const newActionKey = e.target.value;
                        const updatedRouting = { ...step.config?.actionRouting };
                        delete updatedRouting[actionKey];
                        if (newActionKey.trim()) {
                          updatedRouting[newActionKey] = targetStepId;
                        }
                        onUpdateStep(step.id, {
                          config: { ...step.config, actionRouting: updatedRouting }
                        });
                      }}
                      placeholder="Enter action key..."
                      className="w-40 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white text-sm"
                    />
                    <span className="text-gray-400">→</span>
                    <select
                      value={targetStepId || ''}
                      onChange={(e) => {
                        const updatedRouting = {
                          ...step.config?.actionRouting,
                          [actionKey]: e.target.value
                        };
                        onUpdateStep(step.id, {
                          config: { ...step.config, actionRouting: updatedRouting }
                        });
                      }}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white text-sm"
                    >
                      <option value="">Select target step...</option>
                      <option value="end">🏁 END (Workflow Complete)</option>
                      <option value="error">❌ ERROR (Handle Errors)</option>
                      {/* Get available saved steps (excluding current step) */}
                      {(() => {
                        const availableSteps = workflow?.steps?.filter(s => s.id !== step.id && s.saved) || [];
                        return availableSteps.map(nextStep => (
                          <option key={nextStep.id} value={nextStep.id}>
                            {nextStep.name} ({nextStep.type}) {nextStep.isStart && '- START'}
                          </option>
                        ));
                      })()}
                    </select>
                    <button
                      onClick={() => {
                        const updatedRouting = { ...step.config?.actionRouting };
                        delete updatedRouting[actionKey];
                        onUpdateStep(step.id, {
                          config: { ...step.config, actionRouting: updatedRouting }
                        });
                      }}
                      className="px-2 py-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200"
                      title="Remove action mapping"
                    >
                      <FontAwesomeIcon icon={faTrash} size="sm" />
                    </button>
                  </div>
                ))}
              </div>
              
              {/* Add New Action Button */}
              <button
                onClick={() => {
                  const newActionKey = `action_${Object.keys(step.config?.actionRouting || {}).length + 1}`;
                  const updatedRouting = {
                    ...step.config?.actionRouting,
                    [newActionKey]: ''
                  };
                  onUpdateStep(step.id, {
                    config: { ...step.config, actionRouting: updatedRouting }
                  });
                }}
                className="mt-3 px-3 py-2 bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500 text-sm flex items-center space-x-2 transition-colors"
              >
                <FontAwesomeIcon icon={faPlus} />
                <span>Add Action Mapping</span>
              </button>
              
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                {(() => {
                  const savedSteps = workflow?.steps?.filter(s => s.id !== step.id && s.saved) || [];
                  if (savedSteps.length === 0) {
                    return "No saved steps available. Create and save other steps first to connect them.";
                  }
                  return "Define action keys that can be detected in AI responses to route to specific next steps.";
                })()}
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

const WorkflowBuilderV2 = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Workflow state
  const [workflow, setWorkflow] = useState({
    name: '',
    description: '',
    steps: []
  });
  const [expandedSteps, setExpandedSteps] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [prompts, setPrompts] = useState([]);
  const [tools, setTools] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importText, setImportText] = useState('');
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [showToolsModal, setShowToolsModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(null);
  const [selectedTools, setSelectedTools] = useState([]);

  // Load prompts and tools on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load prompts from chat API endpoint
        const token = Cookies.get('sankalvax_token');
        const promptsResponse = await fetch(`${process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai'}/api/chat/prompts`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (promptsResponse.ok) {
          const data = await promptsResponse.json();
          setPrompts(data.prompts || []);
          console.log('Loaded prompts:', data.prompts);
        } else {
          console.error('Failed to fetch prompts:', promptsResponse.status);
        }
        
        // Load tools using direct fetch like in old WorkflowBuilder
        const toolsResponse = await fetch(`${process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai'}/api/tools`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (toolsResponse.ok) {
          const toolsData = await toolsResponse.json();
          setTools(toolsData.tools || []);
          console.log('Loaded tools:', toolsData.tools);
        } else {
          console.error('Failed to fetch tools:', toolsResponse.status);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    loadData();
  }, []);

  // Add new step
  const handleAddStep = () => {
    const stepNumber = workflow.steps.length + 1;
    const newStep = {
      id: generateInternalId(),
      name: `Step ${stepNumber}`,
      type: 'AI',
      config: {
        actionRouting: {}
      },
      isStart: workflow.steps.length === 0
    };
    
    setWorkflow(prev => ({
      ...prev,
      steps: [...prev.steps, newStep]
    }));
    
    // Auto-expand new step
    setExpandedSteps(prev => new Set([...prev, newStep.id]));
    
    toast.success('New step added');
  };

  // Update step
  const handleUpdateStep = (stepId, updates) => {
    setWorkflow(prev => ({
      ...prev,
      steps: prev.steps.map(step => 
        step.id === stepId 
          ? { ...step, ...updates, saved: false }
          : step
      )
    }));
  };

  // Delete step
  const handleDeleteStep = (stepId) => {
    setWorkflow(prev => ({
      ...prev,
      steps: prev.steps.filter(step => step.id !== stepId)
    }));
    
    setExpandedSteps(prev => {
      const newSet = new Set(prev);
      newSet.delete(stepId);
      return newSet;
    });
    
    toast.success('Step deleted');
  };

  // Change step type
  const handleStepTypeChange = (stepId, newType) => {
    setWorkflow(prev => ({
      ...prev,
      steps: prev.steps.map(step =>
        step.id === stepId
          ? { ...step, type: newType, config: { actionRouting: {} }, saved: false }
          : step
      )
    }));
  };

  // Handle step save
  const handleSaveStep = (stepId) => {
    setWorkflow(prev => ({
      ...prev,
      steps: prev.steps.map(step => 
        step.id === stepId ? { ...step, saved: true } : step
      )
    }));
    toast.success('Step saved successfully');
  };

  // Handle view prompt
  const handleViewPrompt = (prompt) => {
    setSelectedPrompt(prompt);
    setShowPromptModal(true);
  };

  // Handle select tools
  const handleSelectTools = (step) => {
    console.log('🔧 handleSelectTools called with step:', step);
    console.log('🔧 Available tools:', tools);
    console.log('🔧 Setting modal state to true');
    
    setCurrentStep(step);
    const configuredTools = step.config?.selected_tools || [];
    setSelectedTools(configuredTools);
    setShowToolsModal(true);
    
    console.log('🔧 Modal state after setting:', showToolsModal);
  };

  // Handle tools change
  const handleToolsChange = (newSelectedTools) => {
    if (currentStep) {
      const updatedConfig = {
        ...currentStep.config,
        selected_tools: newSelectedTools
      };
      handleUpdateStep(currentStep.id, { config: updatedConfig });
    }
    setShowToolsModal(false);
  };

  // Toggle step expansion
  const toggleStepExpansion = (stepId) => {
    setExpandedSteps(prev => {
      const newSet = new Set(prev);
      if (newSet.has(stepId)) {
        newSet.delete(stepId);
      } else {
        newSet.add(stepId);
      }
      return newSet;
    });
  };

  // Save workflow
  const handleSaveWorkflow = async () => {
    if (!workflow.name) {
      toast.error('Please enter a workflow name');
      return;
    }
    
    if (workflow.steps.length === 0) {
      toast.error('Please add at least one step');
      return;
    }
    
    setLoading(true);
    try {
      const workflowData = {
        ...workflow,
        status: 'draft',
        created_by: user?.id
      };
      
      await workflowAPI.saveWorkflow(workflowData);
      toast.success('Workflow saved successfully!');
      navigate('/workflows-v2/list');
    } catch (error) {
      console.error('Error saving workflow:', error);
      toast.error('Failed to save workflow');
    } finally {
      setLoading(false);
    }
  };

  // Export workflow
  const handleExportWorkflow = () => {
    const exportData = {
      name: workflow.name,
      description: workflow.description,
      steps: workflow.steps.map(({ id, saved, ...step }) => step)
    };
    
    const dataStr = JSON.stringify(exportData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `${workflow.name || 'workflow'}_${Date.now()}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
    
    toast.success('Workflow exported successfully!');
  };

  // Import workflow from file
  const handleImportFile = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importData = JSON.parse(e.target.result);
        handleImportData(importData);
      } catch (error) {
        console.error('Import error:', error);
        toast.error('Failed to import workflow. Please check the file format.');
      }
    };
    reader.readAsText(file);
    
    // Reset file input
    event.target.value = '';
  };

  // Handle text import
  const handleTextImport = () => {
    if (!importText.trim()) {
      toast.error('Please enter workflow data to import');
      return;
    }
    
    try {
      const importData = JSON.parse(importText);
      handleImportData(importData);
      setShowImportModal(false);
      setImportText('');
    } catch (error) {
      console.error('Text import error:', error);
      toast.error('Invalid JSON format. Please check your data.');
    }
  };

  // Create mapping of step names to IDs based on actionRouting references
  const createStepIdMapping = (steps) => {
    const stepNameToId = new Map();
    const allActionTargets = new Set();
    
    // Collect all actionRouting targets across all steps
    steps.forEach(step => {
      if (step.config?.actionRouting) {
        Object.values(step.config.actionRouting).forEach(target => {
          if (target && typeof target === 'string' && target !== 'end' && target !== 'error') {
            allActionTargets.add(target);
          }
        });
      }
    });
    
    console.log('📋 All actionRouting targets found:', Array.from(allActionTargets));
    
    // Map step names to their likely IDs based on actionRouting references
    steps.forEach((step, index) => {
      const stepName = step.name;
      
      // Create possible IDs from step name
      const possibleIds = [
        stepName.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, ''),
        stepName.toLowerCase().replace(/\s+/g, ''),
        stepName.toLowerCase().replace(/\s+/g, '_')
      ];
      
      // Look for exact matches first
      let matchedId = null;
      for (const possibleId of possibleIds) {
        if (allActionTargets.has(possibleId)) {
          matchedId = possibleId;
          break;
        }
      }
      
      // If no exact match, try to find the best match among action targets
      if (!matchedId) {
        const targetArray = Array.from(allActionTargets);
        for (const target of targetArray) {
          // Check if any actionRouting target could refer to this step
          const targetWords = target.toLowerCase().split(/[_\s]+/);
          const stepWords = stepName.toLowerCase().split(/\s+/);
          
          // Simple heuristic: if step name contains key words from target
          const hasCommonWords = stepWords.some(stepWord => 
            targetWords.some(targetWord => 
              stepWord.includes(targetWord) || targetWord.includes(stepWord)
            )
          );
          
          if (hasCommonWords) {
            matchedId = target;
            break;
          }
        }
      }
      
      // If still no match, use the generated ID
      const finalId = matchedId || possibleIds[0] || `step_${index + 1}`;
      stepNameToId.set(stepName, finalId);
      
      console.log(`📋 Step "${stepName}" mapped to ID "${finalId}" ${matchedId ? '✅ (found in actionRouting)' : '⚠️ (generated)'}`);
    });
    
    return stepNameToId;
  };

  // Import workflow from text
  const handleImportData = (importData) => {
    try {
      let processedWorkflow;
      
      if (importData.steps && Array.isArray(importData.steps)) {
        // Create step ID mapping based on actionRouting
        const stepIdMapping = createStepIdMapping(importData.steps);
        
        // New format with steps array
        processedWorkflow = {
          name: importData.name || 'Imported Workflow',
          description: importData.description || 'Imported from JSON',
          steps: importData.steps.map((step, index) => {
            const stepName = step.name || `Step ${index + 1}`;
            const stepId = stepIdMapping.get(stepName) || `step_${index + 1}`;
            
            const processedStep = {
              id: stepId, // Use mapped ID instead of generateInternalId()
              name: stepName,
              type: step.type || 'AI',
              config: {
                ...step.config,
                actionRouting: step.config?.actionRouting || {}
              },
              isStart: index === 0
            };
            
            console.log('📥 Import step config preserved:', {
              stepName: processedStep.name,
              stepId: processedStep.id,
              stepType: processedStep.type,
              prompt_id: processedStep.config?.prompt_id,
              provider: processedStep.config?.provider,
              selected_tools: processedStep.config?.selected_tools
            });
            
            return processedStep;
          })
        };
      } else {
        // Try to use processWorkflowImport for other formats
        processedWorkflow = processWorkflowImport(importData);
        
        // Ensure steps is always an array
        if (!processedWorkflow.steps || !Array.isArray(processedWorkflow.steps)) {
          processedWorkflow.steps = [];
        }
      }
      
      setWorkflow(processedWorkflow);
      
      // Debug prompt matching
      console.log('📥 Available prompts:', prompts.map(p => ({ id: p.prompt_id, name: p.name })));
      processedWorkflow.steps.forEach(step => {
        if (step.config?.prompt_id) {
          const matchingPrompt = prompts.find(p => p.prompt_id === step.config.prompt_id);
          console.log(`📥 Step "${step.name}" prompt_id "${step.config.prompt_id}" ${matchingPrompt ? '✅ FOUND' : '❌ NOT FOUND'} in available prompts`);
        }
      });
      
      // Auto-expand all steps
      if (processedWorkflow.steps.length > 0) {
        const allStepIds = new Set(processedWorkflow.steps.map(step => step.id));
        setExpandedSteps(allStepIds);
      }
      
      toast.success(`Workflow imported successfully! ${processedWorkflow.steps.length} steps loaded.`);
    } catch (error) {
      console.error('Import processing error:', error);
      toast.error('Failed to process workflow data. Please check the format.');
    }
  };

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl p-8 mb-8 text-white"
        >
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigate('/workflows-v2')}
              className="flex items-center space-x-2 text-white hover:text-purple-200 transition-colors"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              <span>Back to Workflows</span>
            </button>
            
            <div className="flex items-center space-x-3">
              <label className="px-4 py-2 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-colors cursor-pointer">
                <FontAwesomeIcon icon={faFileExport} className="mr-2" />
                Import
                <input
                  type="file"
                  accept=".json"
                  onChange={handleImportFile}
                  className="hidden"
                />
              </label>
              
              <button
                onClick={() => setShowImportModal(true)}
                className="px-4 py-2 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-colors"
              >
                <FontAwesomeIcon icon={faCopy} className="mr-2" />
                Paste JSON
              </button>
              
              <button
                onClick={handleExportWorkflow}
                disabled={workflow.steps.length === 0}
                className="px-4 py-2 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FontAwesomeIcon icon={faFileExport} className="mr-2" />
                Export
              </button>
              
              <button
                onClick={handleSaveWorkflow}
                disabled={loading || workflow.steps.length === 0}
                className="px-6 py-2 bg-white text-purple-600 rounded-lg hover:bg-purple-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} className="mr-2" />
                    Save Workflow
                  </>
                )}
              </button>
            </div>
          </div>
          
          <h1 className="text-3xl font-bold mb-4">
            <FontAwesomeIcon icon={faProjectDiagram} className="mr-3" />
            Workflow Builder v2
          </h1>
          
          {/* Workflow Details */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-purple-200 mb-1">Workflow ID</label>
              <input
                type="text"
                value={workflow.id || ''}
                onChange={(e) => setWorkflow(prev => ({ ...prev, id: e.target.value }))}
                placeholder="Auto-generated on save"
                className="w-full px-4 py-2 bg-white bg-opacity-20 rounded-lg placeholder-purple-200 text-white focus:outline-none focus:ring-2 focus:ring-white font-mono text-sm"
              />
            </div>
            <div>
              <label className="block text-sm text-purple-200 mb-1">Workflow Name</label>
              <input
                type="text"
                value={workflow.name}
                onChange={(e) => setWorkflow(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter workflow name"
                className="w-full px-4 py-2 bg-white bg-opacity-20 rounded-lg placeholder-purple-200 text-white focus:outline-none focus:ring-2 focus:ring-white"
              />
            </div>
            <div>
              <label className="block text-sm text-purple-200 mb-1">Description</label>
              <input
                type="text"
                value={workflow.description}
                onChange={(e) => setWorkflow(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Enter workflow description"
                className="w-full px-4 py-2 bg-white bg-opacity-20 rounded-lg placeholder-purple-200 text-white focus:outline-none focus:ring-2 focus:ring-white"
              />
            </div>
          </div>
        </motion.div>

        {/* Steps Builder */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <div className="mb-6 flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">
              Workflow Steps
              {workflow.steps.length > 0 && (
                <span className="ml-2 text-sm text-gray-500">
                  ({workflow.steps.length} steps)
                </span>
              )}
            </h2>
            
            <button
              onClick={handleAddStep}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-colors font-medium shadow-lg"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Add Step
            </button>
          </div>

          {/* Steps List */}
          <div className="space-y-4">
            {workflow.steps.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-xl">
                <FontAwesomeIcon icon={faInfoCircle} className="text-4xl text-gray-400 mb-4" />
                <p className="text-gray-500 mb-4">No steps added yet</p>
                <button
                  onClick={handleAddStep}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Add First Step
                </button>
              </div>
            ) : (
              workflow.steps.map((step, index) => (
                <StepCardV2
                  key={step.id}
                  step={step}
                  stepNumber={index + 1}
                  isExpanded={expandedSteps.has(step.id)}
                  onToggleExpand={() => toggleStepExpansion(step.id)}
                  onDelete={handleDeleteStep}
                  onUpdateStep={handleUpdateStep}
                  onStepTypeChange={handleStepTypeChange}
                  isFirst={index === 0}
                  isLast={index === workflow.steps.length - 1}
                  onSaveStep={handleSaveStep}
                  prompts={prompts}
                  onViewPrompt={handleViewPrompt}
                  onSelectTools={handleSelectTools}
                  tools={tools}
                  workflow={workflow}
                />
              ))
            )}
          </div>
        </motion.div>

        {/* Import Modal */}
        {showImportModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">
                  Import Workflow from JSON
                </h2>
                <button
                  onClick={() => {
                    setShowImportModal(false);
                    setImportText('');
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Paste your workflow JSON data below:
                </label>
                <textarea
                  value={importText}
                  onChange={(e) => setImportText(e.target.value)}
                  placeholder={`{
  "name": "My Workflow",
  "description": "Workflow description",
  "steps": [...]
}`}
                  rows={15}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 font-mono text-sm"
                />
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowImportModal(false);
                    setImportText('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleTextImport}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Import Workflow
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </div>

      {/* Prompt Modal */}
      <PromptModal 
        isOpen={showPromptModal}
        onClose={() => setShowPromptModal(false)}
        prompt={selectedPrompt}
      />

      {/* Tools Selection Modal */}
      {console.log('🔧 Rendering ToolsModal with isOpen:', showToolsModal)}
      <ToolsModal
        isOpen={showToolsModal}
        onClose={() => setShowToolsModal(false)}
        tools={tools}
        selectedTools={selectedTools}
        onToolsChange={handleToolsChange}
      />
    </MainLayout>
  );
};

// Tools Selection Modal
const ToolsModal = ({ isOpen, onClose, tools, selectedTools, onToolsChange }) => {
  console.log('🔧 ToolsModal render - isOpen:', isOpen, 'tools count:', tools?.length);
  
  const [tempSelectedTools, setTempSelectedTools] = useState([]);
  
  useEffect(() => {
    if (isOpen) {
      console.log('🔧 ToolsModal opened - selectedTools:', selectedTools);
      setTempSelectedTools([...selectedTools]);
    }
  }, [isOpen, selectedTools]);

  const handleToggleTool = (toolId) => {
    setTempSelectedTools(prev => {
      if (prev.includes(toolId)) {
        return prev.filter(id => id !== toolId);
      } else {
        return [...prev, toolId];
      }
    });
  };

  const handleSave = () => {
    onToolsChange(tempSelectedTools);
    onClose();
  };

  const handleCancel = () => {
    setTempSelectedTools([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            Select Tools
          </h2>
          <button
            onClick={handleCancel}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
          Select the tools that this AI step should have access to.
        </div>

        <div className="space-y-2 mb-6">
          {tools.map(tool => {
            const toolId = tool.tool_definition_id || tool.id;
            const isSelected = tempSelectedTools.includes(toolId);
            
            return (
              <div
                key={toolId}
                className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                  isSelected
                    ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                    : 'border-gray-300 hover:border-purple-300 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700'
                }`}
                onClick={() => handleToggleTool(toolId)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      {tool.tool_name || tool.name}
                    </h4>
                    {tool.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {tool.description}
                      </p>
                    )}
                  </div>
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                    isSelected
                      ? 'bg-purple-500 border-purple-500'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}>
                    {isSelected && (
                      <FontAwesomeIcon icon={faCheckCircle} className="text-white text-xs" />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex justify-end space-x-3">
          <button
            onClick={handleCancel}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700"
          >
            Save Selection ({tempSelectedTools.length} selected)
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default WorkflowBuilderV2;